import {useFormik} from 'formik';
import {useState} from 'react';
import * as Yup from 'yup';
import {StoryScreenModel, StoryParagraphModel, StoryMediaType} from '../../../entities';
import {PreparedImageData} from '../../fundraiserImages';
import {useStoryScreensStore} from './useStoryScreensStore';

interface UseEditStoryScreenFormProps {
  storyId: string;
  screenId: string;
  setOpen: (open: boolean) => void;
  screen: StoryScreenModel;
}

interface StoryScreenFormData {
  paragraphs: StoryParagraphModel[];
  showButton: boolean;
  buttonText: string;
  buttonUrl: string;
  buttonTheme: 'Default' | 'White' | 'Black';
  theme: 'Light' | 'Dark';
  alignToBottom: boolean;
  mediaType: StoryMediaType;
  imageData: PreparedImageData | null;
  imagePreview: string | null;
  videoUploadId: string | null;
  videoPreview: string | null;
}

export const useEditStoryScreenForm = ({storyId, screenId, setOpen, screen}: UseEditStoryScreenFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {updateScreen} = useStoryScreensStore();

  const onSubmit = async (values: StoryScreenFormData) => {
    setErrorMessage(null);
    try {
      // Определяем imageUploadId для изображения
      let imageUploadId: string | null = null;
      if (values.mediaType === 'image') {
        if (values.imageData?.uploadId) {
          // Изображение было изменено — используем новый uploadId
          imageUploadId = values.imageData.uploadId;
        } else if (values.imagePreview && screen.imageUploadId) {
          // Изображение не было изменено — сохраняем существующий imageUploadId
          imageUploadId = screen.imageUploadId;
        }
      }

      // Определяем videoUploadId для видео
      let videoUploadId: string | null = null;
      if (values.mediaType === 'video') {
        if (values.videoUploadId) {
          videoUploadId = values.videoUploadId;
        } else if (values.videoPreview && screen.videoUploadId) {
          videoUploadId = screen.videoUploadId;
        }
      }

      // Фильтруем пустые параграфы перед сохранением
      const filteredParagraphs = values.paragraphs.filter(p => p.text.trim() !== '');

      // Экран мог быть собран из пула (`mediaKey`/`thumbnailMediaKey`). Если при
      // редактировании сменили тип медиа, загрузили новый файл или удалили медиа —
      // экран больше не привязан к пулу, и устаревшие pool-ключи нужно сбросить,
      // иначе они переживут merge в store и уйдут в запрос с уже неактуальным
      // `mediaKind` (или со ссылкой на удалённый из превью материал).
      const mediaTypeChanged = values.mediaType !== screen.mediaType;
      const mediaReplaced = Boolean(imageUploadId) || Boolean(videoUploadId);
      const mediaRemoved =
        values.mediaType === 'image' ? !values.imagePreview : !values.videoPreview;
      const isPoolScreen = Boolean(screen.mediaKey) || Boolean(screen.thumbnailMediaKey);
      const shouldDropPoolKeys = isPoolScreen && (mediaTypeChanged || mediaReplaced || mediaRemoved);

      const poolKeyUpdates: Partial<StoryScreenModel> = shouldDropPoolKeys
        ? {mediaKey: null, thumbnailMediaKey: null}
        : {};

      const updatedScreen: Partial<StoryScreenModel> = {
        mediaType: values.mediaType,
        imageUploadId,
        videoUploadId,
        imagePreview: values.mediaType === 'image' ? values.imagePreview : null,
        videoPreview: values.mediaType === 'video' ? values.videoPreview : null,
        // Если тип медиа изменился — сбрасываем серверное media
        media: values.mediaType !== screen.mediaType ? null : screen.media,
        ...poolKeyUpdates,
        paragraphs: filteredParagraphs,
        button: values.showButton
          ? {
              text: values.buttonText,
              link: values.buttonUrl,
              theme: values.buttonTheme,
            }
          : null,
        theme: values.theme,
        alignToBottom: values.alignToBottom,
      };

      updateScreen(storyId, screenId, updatedScreen);
      setOpen(false);
    } catch (error: any) {
      setErrorMessage(error?.message || 'Произошла ошибка при обновлении экрана');
    }
  };

  // Определяем начальные значения из screen
  const initialMediaType: StoryMediaType = screen.mediaType || screen.media?.type || 'image';
  // Превью — уменьшенная копия (thumbnailUrl), если сервер её отдал
  const serverImage = screen.media?.type === 'image' ? screen.media.image : null;
  const initialImagePreview =
    screen.imagePreview || (serverImage ? serverImage.thumbnailUrl || serverImage.url : null) || null;
  const initialVideoPreview = screen.videoPreview || (screen.media?.type === 'video' ? screen.media.video.url : null) || null;

  const formik = useFormik<StoryScreenFormData>({
    initialValues: {
      paragraphs: screen.paragraphs || [
        {style: 'h1', text: ''},
        {style: 'p2', text: ''},
      ],
      showButton: !!screen.button,
      buttonText: screen.button?.text || '',
      buttonUrl: screen.button?.link || '',
      buttonTheme: screen.button?.theme || 'Default',
      theme: screen.theme || 'Light',
      alignToBottom: screen.alignToBottom || false,
      mediaType: initialMediaType,
      imageData: null,
      imagePreview: initialImagePreview,
      videoUploadId: screen.videoUploadId || null,
      videoPreview: initialVideoPreview,
    },
    validationSchema: Yup.object({
      paragraphs: Yup.array()
        .of(
          Yup.object({
            style: Yup.string().required(),
            text: Yup.string(),
          }),
        )
        .test('has-text', 'Необходимо добавить хотя бы один параграф с текстом', function (paragraphs) {
          return paragraphs?.some((p) => (p?.text || '').trim() !== '') || false;
        }),
      buttonText: Yup.string().when('showButton', {
        is: true,
        then: (schema) => schema.required('Введите текст кнопки'),
      }),
      buttonUrl: Yup.string().when('showButton', {
        is: true,
        then: (schema) => schema.required('Введите URL кнопки'),
      }),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit,
  });

  return {
    ...formik,
    isLoading: false,
    submitError: undefined,
    errorMessage,
    submitForm: () => formik.handleSubmit(),
    isValid: getIsFormValid(formik.values),
  };
};

const getIsFormValid = (values: StoryScreenFormData): boolean => {
  const hasTextInParagraphs = values.paragraphs?.some((p) => (p?.text || '').trim() !== '');

  const buttonValid = !values.showButton || (values.buttonText.trim() !== '' && values.buttonUrl.trim() !== '');
  return hasTextInParagraphs && buttonValid;
};
